import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Slider,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { CallRecording } from "../../../types/artifact";
import { formatDate, formatDurationMmSs, MS_PER_SECOND } from "../../../utils/date";

type CallRecordingPlayerProps = {
  recordings: CallRecording[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  onSeekRef?: React.MutableRefObject<((timestamp: number) => void) | null>;
  onTimeUpdate?: (currentTime: number) => void;
  hasTranscript?: boolean;
};

export const CallRecordingPlayer = ({
  recordings,
  isLoading,
  error,
  onRefresh,
  onSeekRef,
  onTimeUpdate,
  hasTranscript = false
}: CallRecordingPlayerProps): React.ReactElement => {
  const [selectedRecording, setSelectedRecording] = useState<CallRecording | null>(
    recordings.length > 0 ? recordings[0] : null
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Update selectedRecording when recordings arrive
  useEffect(() => {
    if (recordings.length > 0 && !selectedRecording) {
      setSelectedRecording(recordings[0]);
      // Initialize duration from the recording metadata
      if (recordings[0]?.duration) {
        setDuration(recordings[0].duration);
      }
    }
  }, [recordings, selectedRecording]);

  // Function to seek to a specific timestamp and start playing
  const seekToTimestamp = useCallback((timestamp: number) => {
    if (audioRef.current && selectedRecording) {
      // Ensure timestamp is within valid range
      const validTime = Math.min(Math.max(0, timestamp), duration || selectedRecording.duration);
      audioRef.current.currentTime = validTime;
      setCurrentTime(validTime);
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [duration, selectedRecording]);

  // Expose the seekToTimestamp function via ref if provided
  useEffect(() => {
    if (onSeekRef) {
      onSeekRef.current = seekToTimestamp;
    }

    // Cleanup function
    return () => {
      if (onSeekRef) {
        onSeekRef.current = null;
      }
    };
  }, [onSeekRef, seekToTimestamp]);

  // Handle recording selection change
  const handleRecordingChange = (recording: CallRecording) => {
    setSelectedRecording(recording);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(recording.duration);
    setMenuAnchorEl(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // Handle play/pause button click
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle audio element events
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  // Handle timeupdate event to update current time
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const newTime = audioRef.current.currentTime;
      setCurrentTime(newTime);

      // Notify parent component of time update
      if (onTimeUpdate) {
        onTimeUpdate(newTime);
      }
    }
  };

  // Handle loadedmetadata event to get duration
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  // Handle user changing the slider position
  const handleSeek = (_event: Event, newValue: number | number[]) => {
    const value = typeof newValue === "number" ? newValue : newValue[0];
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const formatPlaybackTime = (durationInSeconds: number) =>
    formatDurationMmSs(durationInSeconds * MS_PER_SECOND);

  if (isLoading) {
    return (
      <>
        <Skeleton variant="text" width={98} height={26} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={45} sx={{ mb: 3 }} />
      </>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 3 }}>
        {
          hasTranscript ? (
            <Alert severity="info">
              No recording available for this transcript
            </Alert>
          ) : (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={onRefresh}>
                  Retry
                </Button>
              }>
              Error loading call recordings: {error}
            </Alert>
          )
        }
      </Box>
    );
  }

  if (recordings.length === 0) {
    return <></>; // Don't show the player if there are no recordings
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary">
            Call Recording
            {recordings.length > 1 && ` (${recordings.indexOf(selectedRecording!) + 1}/${recordings.length})`}
          </Typography>

          {recordings.length > 1 && (
            <>
              <IconButton
                aria-label="more options"
                aria-controls="recording-menu"
                aria-haspopup="true"
                onClick={handleMenuOpen}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="recording-menu"
                anchorEl={menuAnchorEl}
                keepMounted
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
              >
                {recordings.map((recording, index) => (
                  <MenuItem
                    key={recording.id}
                    onClick={() => handleRecordingChange(recording)}
                    selected={selectedRecording?.id === recording.id}
                  >
                    Recording {index + 1}
                    {recording.startedAt && (
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        ({formatDate(new Date(recording.startedAt), "h:mm a")})
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Stack>

        {selectedRecording && (
          <Box sx={{ mb: 1 }}>
            <audio
              ref={audioRef}
              src={selectedRecording.url}
              onEnded={handleAudioEnded}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              style={{ display: "none" }}
            />

            <Stack direction="column" spacing={1} width="100%">
              <Stack direction="row" spacing={2} alignItems="center">
                <IconButton
                  onClick={togglePlayPause}
                  color="primary"
                  aria-label={isPlaying ? "pause" : "play"}
                  size="large"
                >
                  {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>

                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    {formatPlaybackTime(currentTime)}
                  </Typography>
                  <Slider
                    size="small"
                    value={currentTime}
                    min={0}
                    max={duration || selectedRecording.duration}
                    onChange={handleSeek}
                    aria-label={"Audio progress"}
                    sx={{ mx: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {formatPlaybackTime(duration || selectedRecording.duration)}
                  </Typography>
                </Box>

                <Tooltip title="Restart">
                  <IconButton
                    onClick={() => {
                      if (audioRef.current) {
                        audioRef.current.currentTime = 0;
                        if (!isPlaying) {
                          audioRef.current.play();
                          setIsPlaying(true);
                        }
                      }
                    }}
                    size="small"
                  >
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Download recording">
                  <IconButton
                    component="a"
                    href={selectedRecording.url}
                    download={`recording-${selectedRecording.id}.mp3`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CallRecordingPlayer;
